/**
 * Hilo de comentarios estilo GitHub Discussions / Notion.
 * - 1 nivel de respuestas
 * - Markdown básico via render con ReactMarkdown
 * - Menciones simples @usuario (autocomplete contra /api/comments/mentions/users)
 * - Edición y eliminación según permisos
 */
import { useState } from 'react';
import { MessageCircle, Reply, Pencil, Trash2, Send, AtSign } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Markdown } from '@/components/common/Markdown';
import { useAuth } from '@/features/auth/AuthProvider';
import {
  useComments,
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
} from './queries';
import { initials, timeAgo, cn } from '@/lib/utils';
import type { Comment } from '@/lib/types';

interface Props {
  articleId?: string;
  subsectionId?: string;
}

export function Comments({ articleId, subsectionId }: Props) {
  const target = { articleId, subsectionId };
  const { data, isLoading } = useComments(target);
  const create = useCreateComment(target);
  const [draft, setDraft] = useState('');

  const submit = async () => {
    if (!draft.trim()) return;
    await create.mutateAsync({ contenidoMd: draft });
    setDraft('');
  };

  const total =
    (data?.comments?.length ?? 0) +
    (data?.comments?.reduce((acc, c) => acc + (c.replies?.length ?? 0), 0) ?? 0);

  return (
    <section className="border-t pt-8">
      <div className="mb-4 flex items-center gap-2">
        <MessageCircle className="h-5 w-5" />
        <h2 className="text-lg font-semibold">Comentarios</h2>
        {total > 0 && <Badge variant="secondary">{total}</Badge>}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (
        <div className="space-y-5">
          {(data?.comments ?? []).map((c) => (
            <CommentNode key={c.id} comment={c} target={target} />
          ))}
          {(data?.comments?.length ?? 0) === 0 && (
            <div className="bg-muted/30 text-muted-foreground rounded-lg border p-6 text-center text-sm">
              Sé la primera persona en comentar.
            </div>
          )}
        </div>
      )}

      <div className="bg-card mt-6 rounded-lg border p-3">
        <CommentInput
          value={draft}
          onChange={setDraft}
          onSubmit={submit}
          submitting={create.isPending}
          placeholder="Escribe un comentario. Usa @ para mencionar a alguien."
        />
      </div>
    </section>
  );
}

function CommentNode({
  comment,
  target,
}: {
  comment: Comment;
  target: { articleId?: string; subsectionId?: string };
}) {
  const { user } = useAuth();
  const update = useUpdateComment(target);
  const remove = useDeleteComment(target);
  const reply = useCreateComment(target);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyDraft, setReplyDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(comment.contenidoMd);

  const canEdit = user?.id === comment.autorId;
  const canDelete = canEdit || user?.rol === 'admin';

  return (
    <article className="bg-card flex gap-3 rounded-lg border p-4">
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback>{initials(comment.autorNombre)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <header className="flex items-center gap-2 text-sm">
          <span className="font-medium">{comment.autorNombre}</span>
          <span className="text-muted-foreground text-xs">· {timeAgo(comment.createdAt)}</span>
          {comment.editado && (
            <span className="text-muted-foreground text-xs italic">· editado</span>
          )}
        </header>

        {editing ? (
          <div className="mt-2">
            <Textarea value={editDraft} onChange={(e) => setEditDraft(e.target.value)} rows={3} />
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                onClick={async () => {
                  await update.mutateAsync({ id: comment.id, contenidoMd: editDraft });
                  setEditing(false);
                }}
              >
                Guardar
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <Markdown content={comment.contenidoMd} className="mt-1 text-sm" />
        )}

        <div className="mt-2 flex gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={() => setReplyOpen((v) => !v)}
          >
            <Reply className="mr-1 h-3 w-3" /> Responder
          </Button>
          {canEdit && !editing && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={() => setEditing(true)}
            >
              <Pencil className="mr-1 h-3 w-3" /> Editar
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive h-7 px-2 text-xs"
              onClick={() => {
                if (confirm('¿Eliminar este comentario?')) remove.mutate(comment.id);
              }}
            >
              <Trash2 className="mr-1 h-3 w-3" /> Eliminar
            </Button>
          )}
        </div>

        {replyOpen && (
          <div className="mt-3 rounded-md border p-2">
            <CommentInput
              value={replyDraft}
              onChange={setReplyDraft}
              onSubmit={async () => {
                if (!replyDraft.trim()) return;
                await reply.mutateAsync({ contenidoMd: replyDraft, parentId: comment.id });
                setReplyDraft('');
                setReplyOpen(false);
              }}
              submitting={reply.isPending}
              placeholder={`Responder a ${comment.autorNombre}…`}
              compact
            />
          </div>
        )}

        {comment.replies && comment.replies.length > 0 && (
          <div className="border-border mt-4 space-y-3 border-l-2 pl-4">
            {comment.replies.map((r) => (
              <ReplyNode key={r.id} comment={r} target={target} />
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

function ReplyNode({
  comment,
  target,
}: {
  comment: Comment;
  target: { articleId?: string; subsectionId?: string };
}) {
  const { user } = useAuth();
  const update = useUpdateComment(target);
  const remove = useDeleteComment(target);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.contenidoMd);
  const canEdit = user?.id === comment.autorId;
  const canDelete = canEdit || user?.rol === 'admin';

  return (
    <div className="flex gap-2 text-sm">
      <Avatar className="h-7 w-7 shrink-0">
        <AvatarFallback>{initials(comment.autorNombre)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{comment.autorNombre}</span>
          <span className="text-muted-foreground text-xs">· {timeAgo(comment.createdAt)}</span>
        </div>
        {editing ? (
          <>
            <Textarea
              className="mt-1"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
            />
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                onClick={async () => {
                  await update.mutateAsync({ id: comment.id, contenidoMd: draft });
                  setEditing(false);
                }}
              >
                Guardar
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancelar
              </Button>
            </div>
          </>
        ) : (
          <Markdown content={comment.contenidoMd} className="mt-0.5" />
        )}
        <div className="mt-1 flex gap-1">
          {canEdit && !editing && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-1.5 text-xs"
              onClick={() => setEditing(true)}
            >
              Editar
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="ghost"
              className={cn('text-destructive h-6 px-1.5 text-xs')}
              onClick={() => {
                if (confirm('¿Eliminar este comentario?')) remove.mutate(comment.id);
              }}
            >
              Eliminar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

interface InputProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  submitting?: boolean;
  placeholder?: string;
  compact?: boolean;
}

function CommentInput({ value, onChange, onSubmit, submitting, placeholder, compact }: InputProps) {
  return (
    <div>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={compact ? 2 : 3}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          }
        }}
        className="text-sm"
      />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-muted-foreground flex items-center gap-1 text-xs">
          <AtSign className="h-3 w-3" /> Markdown soportado · ⌘/Ctrl + Enter para enviar
        </span>
        <Button size="sm" onClick={onSubmit} disabled={submitting || !value.trim()}>
          <Send className="mr-1 h-3 w-3" /> Comentar
        </Button>
      </div>
    </div>
  );
}
